import getRouterBasename from '@/lib/router';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppApi } from '@/customApiClients/appApiClient/Api';

interface Organization {
  id: string;
  name: string;
  alias?: string;
}

interface OrganizationsPageProps {}

const Organizations = ({}: OrganizationsPageProps) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selecting, setSelecting] = useState(false);
  const navigate = useNavigate();

  // Initialize API client
  const appApiClient = useMemo(
    () =>
      new AppApi({
        baseUrl: window.location.origin,
        baseApiParams: {
          credentials: 'include',
        },
      }),
    []
  );

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        
        const response = await appApiClient.auth.getUserOrganizations();
        
        console.log('Organizations API response:', {
          ok: response.ok,
          status: response.status,
          data: response.data,
          error: response.error
        });

        if (!response.ok) {
          if (response.status === 401) {
            // No pending token - redirect back to login
            window.location.href = '/login';
            return;
          }
          if (response.status === 403) {
            // User doesn't have required role - redirect to access denied page
            console.log('Access denied (403) - redirecting to access-denied page');
            window.location.href = getRouterBasename() + '/access-denied';
            return;
          }
          // Check if error has more details
          const errorMessage = response.error?.detail || response.error?.message || 'Failed to fetch organizations';
          throw new Error(errorMessage);
        }

        // Check if response.data exists
        if (!response.data) {
          throw new Error('No data received from server');
        }

        // Cast the organizations to the proper type
        const orgs = (response.data.organizations || []) as Organization[];
        setOrganizations(orgs);
        
        // Show message if Keycloak isn't configured with organizations
        if (response.data.message) {
          setError(response.data.message);
        }
      } catch (err: any) {
        console.error('Error fetching organizations:', err);
        
        // Check if this is a 401 error from the API client
        if (err.status === 401 || err.data?.status === 401) {
          console.log('401 error in catch block, redirecting to login');
          window.location.href = '/login';
          return;
        }
        
        // Check if this is a 403 error - access denied
        if (err.status === 403 || err.data?.status === 403) {
          console.log('403 error in catch block, redirecting to access-denied');
          window.location.href = getRouterBasename() + '/access-denied';
          return;
        }
        
        // Handle thrown response from API client
        if (err.data) {
          const errorDetail = err.error?.detail || err.data?.detail || err.message;
          setError(errorDetail || 'Failed to load organizations');
        } else {
          setError(err.message || 'Failed to load organizations');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [navigate, appApiClient]);

  const handleSelectOrganization = async (org: Organization) => {
    try {
      setSelecting(true);
      setError(null);

      const response = await appApiClient.auth.selectOrganization({
        organizationId: org.id,
        organizationName: org.name,
        organizationAlias: org.alias || null,
      });

      if (!response.ok) {
        // Handle 401 - token expired, redirect to login
        if (response.status === 401) {
          setError('Your session has expired. Redirecting to login...');
          setTimeout(() => window.location.href = '/login', 2000);
          return;
        }
        
        // Handle 403 - access denied
        if (response.status === 403) {
          console.log('403 on selectOrganization - redirecting to access-denied');
          window.location.href = getRouterBasename() + '/access-denied';
          return;
        }
        
        const errorMessage = typeof response.error?.detail === 'string' 
          ? response.error.detail 
          : 'Failed to select organization';
        throw new Error(errorMessage);
      }

      if (!response.data) {
        throw new Error('No data received from server');
      }

      if (response.data.success) {
        // Token has been exchanged and cookie updated
        // Store organization ID in localStorage for organization-specific state
        localStorage.setItem('selected-organization-id', org.id);
        console.log('Organization selected successfully, stored org ID:', org.id);
        
        // Add a small delay to ensure cookie is fully set, then reload
        setTimeout(() => {
          // Force a hard reload to ensure new cookie is picked up
          window.location.href = '/';
        }, 1000);
      } else {
        throw new Error('Organization selection was not successful');
      }
    } catch (err: any) {
      console.error('Error selecting organization:', err);
      
      // Check if this is a 401 error from the API client
      if (err.status === 401 || err.data?.status === 401) {
        console.log('401 error in catch block, redirecting to login');
        setError('Your session has expired. Redirecting to login...');
        setTimeout(() => window.location.href = '/login', 2000);
        return;
      }
      
      // Check if this is a 403 error - access denied
      if (err.status === 403 || err.data?.status === 403) {
        console.log('403 error in catch block, redirecting to access-denied');
        window.location.href = getRouterBasename() + '/access-denied';
        return;
      }
      
      setError(err.message || 'Failed to select organization');
      setSelecting(false);
    }
  };

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6 bg-background">

      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-semibold tracking-tight">
            Select Your Organization
          </h2>
          <p className="text-sm text-muted-foreground">
            You belong to multiple organizations. Please select one to continue.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
              <span className="absolute! -m-px! h-px! w-px! overflow-hidden! whitespace-nowrap! border-0! p-0! [clip:rect(0,0,0,0)]!">
                Loading...
              </span>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">Loading organizations...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {organizations.sort((a, b) => a.name.localeCompare(b.name)).map((org) => (
              <button
                key={org.id}
                onClick={() => handleSelectOrganization(org)}
                disabled={selecting}
                className={`
                  w-full rounded-lg border-2 px-6 py-4 text-left text-base font-medium
                  transition-all duration-200
                  ${
                    selecting
                      ? 'cursor-not-allowed opacity-50 bg-muted'
                      : 'cursor-pointer hover:border-foreground hover:bg-accent border-border'
                  }
                `}
              >
                <div>
                  <div className="font-medium">{org.name}</div>
                  {org.alias && org.alias !== org.name && (
                    <div className="text-sm text-muted-foreground mt-1">{org.alias}</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {!loading && organizations.length === 0 && !error && (
          <div className="rounded-lg border border-yellow-500/50 bg-yellow-500/10 p-4 text-sm">
            <p className="font-medium">No organizations found</p>
            <p className="mt-1 text-sm opacity-80">
              Please contact your administrator for assistance.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Organizations;

